/**
 * Mobile view (below `lg`) of the check-inventory modal, rendered by `./CheckInventoryModal.tsx`:
 * a full-height slide-in panel (`SliderMenu` from
 * `components/Helper/commonStyles.tsx`) with the batch rows (`./CheckInventoryHelper.tsx` →
 * `renderMobileBatchData`) or the "not available locally" message.
 *
 * Unlike the desktop view, the Back, X and Cancel controls only set the open flag to false; the
 * effect on `showCheckInventoryModal` then restores the page's body style and calls
 * `handleOnClose`, which reports back to the caller. While open, the body's scrolling is turned
 * off.
 */
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import styled from "@emotion/styled";
import React, { createRef, RefObject, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { SliderMenu } from "@components/Helper/commonStyles";
import { getDisplayUom, useWindowSize } from "@ryerson/frontend.common";
import { Typography } from "@ryerson/frontend.typography";
import rgba from "polished/lib/color/rgba";
import { Icon } from "@ryerson/frontend.assets";
import { useApplication } from "@ryerson/frontend.application";
import {
	CheckInventoryModalProps,
	renderItemNotAvailableLocallyMessage,
	renderMobileBatchData,
} from "./CheckInventoryHelper";
import { lbs } from "@ryerson/common.uom";
import { Button } from "@ryerson/frontend.button";

const GlobalNavigation = styled.div`
	height: 50px;
	width: 100%;
	top: 0;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.lightGray};
			background-color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

const closeFiltersPane = (props: any) => {
	const { theme } = props;
	return css`
		height: 50px;
		width: 50px;
		border-left: 1px solid ${rgba(theme.colors.primary.white, 0.1)};
	`;
};

const TableContainer = styled(Flex)`
	${(props: any) => {
		const { borderColor, backgroundColor } = props;
		return css`
			border-left: 1px solid ${borderColor};
			border-right: 1px solid ${borderColor};
			border-bottom: 1px solid ${borderColor};
			background: ${backgroundColor};
		`;
	}}
`;

/**
 * Batch-level inventory in a slide-in panel. Its controls only call `setShowCheckInventoryModal`;
 * the effect on `showCheckInventoryModal` runs `handleOnClose`.
 */
const CheckInventoryModalMobile: React.FC<CheckInventoryModalProps> = ({
	showCheckInventoryModal,
	content,
	batches,
	batchQtyUom,
	batchPriceUom,
	product,
	fetching,
	handleOnClose,
	setShowCheckInventoryModal,
	showItemNotAvailableLocallyMessage,
}) => {
	const { theme } = useTheme();
	const {
		isNative,
		localization: { country },
	} = useApplication();

	const { gray, white, darkerGray, lightGray, lighterGray, header, darkGray } =
		theme.colors.primary;

	const [hasMounted, setHasMounted] = React.useState<boolean>(false);
	const [domNodeRefs] = useState<any>({});
	const [bodyStyles, setBodyStyles] = useState<string>("");
	const windowSize = useWindowSize();

	const creatDomNodeRef = (fieldName: string): RefObject<any> => {
		domNodeRefs[fieldName] = createRef();
		return domNodeRefs[fieldName];
	};

	// Manage slider animation and body scrolling
	useEffect(() => {
		if (hasMounted) {
			const sliderEl = domNodeRefs["check-inventory-modal"].current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			const body = document?.querySelector("body");
			if (showCheckInventoryModal) {
				(window as any).scrollTo(0, 0);
				setBodyStyles(body?.getAttribute("style") || "");
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 300);
			} else {
				body && body.setAttribute("style", bodyStyles);
				setShowCheckInventoryModal(false);
				handleOnClose();
			}
		}
	}, [showCheckInventoryModal]);

	// Mounting this view closes the modal through the caller's setter.
	useEffect(() => {
		setHasMounted(true);
		setShowCheckInventoryModal(false);
	}, []);

	return (
		<>
			<SliderMenu
				{...{ theme: theme }}
				ref={creatDomNodeRef(`check-inventory-modal`)}
				className="check-inventory-modal slide-close"
				css={{ top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 100 }}
			>
				<Flex css={{ height: windowSize[1] - 100, overflowY: "scroll" }} direction="column">
					<GlobalNavigation theme={theme}>
						<Flex alignItems="center">
							<FlexItem grow={1}>
								<Flex
									justifyContent="flex-start"
									onClick={() => setShowCheckInventoryModal(false)}
								>
									<FlexItem alignSelf="center" css={{ marginLeft: 20 }}>
										<Icon
											icon="chevron-left"
											size="md"
											onClick={() => setShowCheckInventoryModal(false)}
											color={white}
											circled={true}
											css={{ borderColor: gray }}
										/>
									</FlexItem>
									<FlexItem
										css={css`
											padding-left: 10px;
										`}
									>
										<Typography
											variant="p"
											color={theme.colors.tertiary.label}
											size="md"
										>
											{content?.back}
										</Typography>
									</FlexItem>
								</Flex>
							</FlexItem>
							<FlexItem>
								<Flex
									style={closeFiltersPane({ theme: theme })}
									justifyContent="center"
									alignItems="center"
								>
									<Icon
										icon="close"
										size="sm"
										onClick={() => setShowCheckInventoryModal(false)}
										color={theme.colors.primary.white}
									/>
								</Flex>
							</FlexItem>
						</Flex>
					</GlobalNavigation>
					<ContentSection type="secondary">
						<Typography variant="h3" css={{ paddingBottom: "20px" }}>
							{content.batchLevelInfo}
						</Typography>
						{showItemNotAvailableLocallyMessage ? (
							renderItemNotAvailableLocallyMessage(content, theme, country)
						) : (
							<>
								{/* <Flex
									justifyContent="center"
									alignItems="center"
									css={{
										background: gray,
										height: "65px",
										padding: "0 10px",
									}}
								>
									<Typography
										size="md"
										css={{ paddingRight: "10px" }}
										color={white}
									>
										{content.iNeed}
									</Typography>
									<FlexItem css={{ width: "140px", marginRight: "10px" }}>
										<NumberInput
											value={
												Number(batchDisplayQuantity) !== 0
													? batchDisplayQuantity
															?.toString()
															.replace(/^0+/, "")
													: batchDisplayQuantity?.toString()
											}
											onChange={(event) =>
												batchHandleOnChangeQty(event.target.value)
											}
											size="sm"
											language={language}
											dataTestId="qty"
										/>
									</FlexItem>
									<FlexItem css={{ width: "80px" }}>
										<Dropdown
											id="uom"
											value={batchQtyUom}
											options={qtyUomDropdown}
											onChange={(value) =>
												batchUpdateQuantityUom(value as UOM)
											}
											size="sm"
											disabled={qtyUomDropdown.length === 1}
											language={language}
										/>
									</FlexItem>
								</Flex> */}
								<TableContainer
									{...{ borderColor: lightGray, backgroundColor: white }}
									direction="column"
								>
									<Flex
										css={{
											height: "52px",
											padding: "0 10px",
											background: lighterGray,
											borderBottom: `1px solid ${gray}`,
										}}
										justifyContent="space-between"
										alignItems="center"
									>
										<Typography color={header} size="md">
											{content.batch}
										</Typography>
										<Typography color={header} size="md">
											{getDisplayUom(
												product?.form,
												batchQtyUom || lbs.value,
												"quantity"
											)}
										</Typography>
										<Typography
											color={header}
											css={{ textAlign: "right" }}
											size="md"
										>
											{content.available}
										</Typography>
									</Flex>
									{batches.map((batch, index: number) => {
										return renderMobileBatchData(
											index,
											fetching,
											batch,
											batchQtyUom,
											batchPriceUom,
											product,
											darkerGray,
											content
										);
									})}
								</TableContainer>
								<Flex
									css={{ marginTop: 20, width: "100%" }}
									justifyContent="center"
								>
									<Button
										css={{ width: "100%" }}
										label={content.cancel}
										onClick={() => setShowCheckInventoryModal(false)}
										paddingType="relaxed"
										fullwidth
										foreground="light"
										background={darkGray}
										dataTestId="check-inventory-modal-mobile-cancel-button"
									/>
								</Flex>
							</>
						)}
					</ContentSection>
				</Flex>
			</SliderMenu>
		</>
	);
};

export default CheckInventoryModalMobile;
